import { Camera } from "./Camera";
import { Input } from "./Input";
import { Renderer } from "./Renderer";
import { World } from "./World";
import {
  fetchLeaderboard,
  loadStoredName,
  sanitizeName,
  startSession,
  storeName,
  submitScore,
  type LeaderboardEntry,
  type StartSessionResponse,
} from "../api/leaderboard";

type State = "title" | "playing" | "dead";

const ROSIE_CTA_URL = "https://www.jethotelsolutions.com/robotics-tailos/";
const ROBOT_MODE_MS = 5000;
const ROSIE_TALITY_MS = 1500;

export class Game {
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private hudEl: HTMLElement;
  private bannerEl: HTMLElement;
  private overlayEl: HTMLElement;
  private renderer: Renderer;
  private camera: Camera;
  private world: World;
  private input: Input;

  private lastTime = 0;
  private rafId = 0;
  private running = false;
  private state: State = "title";

  private playerName = "";
  private session: StartSessionResponse | null = null;
  private cachedTop: LeaderboardEntry[] = [];

  /** Wall-clock ms at which the most recent Rosie-tality was triggered. */
  private rosieTalityAt = 0;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.innerHTML = "";
    this.root.className = "relative w-full h-full";

    this.canvas = document.createElement("canvas");
    this.canvas.className = "absolute inset-0";
    this.root.appendChild(this.canvas);

    this.hudEl = document.createElement("div");
    this.hudEl.className =
      "absolute top-0 left-0 right-0 p-3 sm:p-5 pointer-events-none z-10";
    this.hudEl.style.paddingTop = "max(0.75rem, env(safe-area-inset-top))";
    this.root.appendChild(this.hudEl);

    this.bannerEl = document.createElement("div");
    this.bannerEl.className =
      "absolute inset-x-0 top-[28%] flex justify-center pointer-events-none z-30";
    this.root.appendChild(this.bannerEl);

    this.overlayEl = document.createElement("div");
    this.overlayEl.className =
      "absolute inset-0 flex flex-col items-center justify-center bg-black/65 backdrop-blur-sm z-20";
    this.root.appendChild(this.overlayEl);

    this.renderer = new Renderer(this.canvas);
    this.camera = new Camera();
    this.world = new World();
    this.input = new Input(this.canvas);

    this.playerName = loadStoredName();

    this.handleResize();
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("orientationchange", this.handleResize);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.showTitleScreen();
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  private async beginRun(): Promise<void> {
    this.world = new World();
    this.state = "playing";
    this.rosieTalityAt = 0;
    this.bannerEl.innerHTML = "";
    this.hideOverlay();
    this.session = await startSession();
  }

  private handleResize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.resize(w, h);
    this.camera.setViewport(w, h);
  };

  private tick = (now: number): void => {
    if (!this.running) return;
    const dt = Math.min(0.05, (now - this.lastTime) / 1000);
    this.lastTime = now;

    if (this.state === "playing") {
      this.updatePlayerInput();
      this.world.update(dt);

      // React to events the world emitted this frame.
      for (const death of this.world.events.deaths) {
        if (death.killer === this.world.player && death.victim !== this.world.player) {
          this.triggerRosieTality(death.victim.name);
        }
      }
      if (this.world.events.robotModeTriggered) {
        this.triggerRobotModeFlash();
      }

      this.camera.follow(
        this.world.player,
        this.world.player.heading,
        dt,
      );

      if (!this.world.player.alive) {
        this.state = "dead";
        this.showGameOver();
      }
    } else {
      this.world.update(dt);
      const t = now / 5000;
      this.camera.x = Math.cos(t) * 300;
      this.camera.y = Math.sin(t * 0.7) * 300;
    }

    this.renderer.draw(this.world, this.camera, now);
    this.drawHud(now);

    this.rafId = requestAnimationFrame(this.tick);
  };

  private updatePlayerInput(): void {
    if (!this.input.pointerActive) return;
    const screen = this.camera.worldToScreen(
      this.world.player.x,
      this.world.player.y,
    );
    const dx = this.input.pointerX - screen.x;
    const dy = this.input.pointerY - screen.y;
    if (dx * dx + dy * dy < 100) return;
    this.world.player.desiredHeading = Math.atan2(dy, dx);
  }

  private drawHud(now: number): void {
    if (this.state !== "playing") {
      this.hudEl.innerHTML = "";
      return;
    }
    const p = this.world.player;
    const rank = this.rankOfPlayer();
    const invincibleRemaining = Math.max(0, p.invincibleUntil - now);
    const invincibleFrac = invincibleRemaining / ROBOT_MODE_MS;

    // Progress toward next Robot Mode (when not currently invincible).
    const range = this.world.nextRobotThreshold - this.world.prevRobotThreshold;
    const into = Math.max(0, p.score - this.world.prevRobotThreshold);
    const robotProgressFrac = Math.min(1, into / range);

    this.hudEl.innerHTML = `
      <div class="flex items-start justify-between gap-3">
        <div class="rounded-2xl bg-black/45 backdrop-blur px-4 py-2.5 border border-white/10">
          <div class="text-[10px] uppercase tracking-widest text-[#8eece0]/80">Score</div>
          <div class="text-3xl font-semibold tabular-nums leading-none mt-0.5">${p.score}</div>
          ${
            p.kills > 0
              ? `<div class="text-[10px] uppercase tracking-widest text-[#ff8d6b]/80 mt-1">
                   ${p.kills} kill${p.kills === 1 ? "" : "s"}
                 </div>`
              : ""
          }
        </div>
        <div class="rounded-2xl bg-black/45 backdrop-blur px-4 py-2.5 border border-white/10 text-right">
          <div class="text-[10px] uppercase tracking-widest text-[#8eece0]/80">Rank</div>
          <div class="text-3xl font-semibold tabular-nums leading-none mt-0.5">#${rank}</div>
        </div>
      </div>
      <div class="mt-3 mx-auto max-w-xs">
        ${
          invincibleFrac > 0
            ? `
            <div class="text-center text-[10px] uppercase tracking-[0.3em] text-[#8eece0] mb-1">
              Robot Mode · ${(invincibleRemaining / 1000).toFixed(1)}s
            </div>
            <div class="h-1.5 bg-black/45 rounded-full overflow-hidden border border-white/10">
              <div class="h-full bg-gradient-to-r from-[#6fd6c8] to-[#b9fff0]"
                style="width:${invincibleFrac * 100}%"></div>
            </div>`
            : `
            <div class="text-center text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
              Next Robot Mode · ${into} / ${range}
            </div>
            <div class="h-1.5 bg-black/45 rounded-full overflow-hidden border border-white/10">
              <div class="h-full bg-[#6fd6c8]/60"
                style="width:${robotProgressFrac * 100}%"></div>
            </div>`
        }
      </div>
    `;
  }

  private rankOfPlayer(): number {
    let rank = 1;
    for (const b of this.world.bots) {
      if (b.score > this.world.player.score) rank++;
    }
    return rank;
  }

  /** Animated banner: "ROSIE-TALITY!" with the killed bot's name. */
  private triggerRosieTality(victimName: string): void {
    this.rosieTalityAt = performance.now();
    // Forcibly restart the CSS animation by removing + re-adding the node.
    this.bannerEl.innerHTML = `
      <div class="rosie-tality-banner">
        <div class="rosie-tality-text">ROSIE-TALITY!</div>
        <div class="rosie-tality-sub">${escapeHtml(victimName)} eliminated</div>
      </div>
    `;
    // Schedule cleanup so the DOM stays empty after the animation.
    window.setTimeout(() => {
      if (performance.now() - this.rosieTalityAt >= ROSIE_TALITY_MS - 30) {
        this.bannerEl.innerHTML = "";
      }
    }, ROSIE_TALITY_MS);
  }

  private triggerRobotModeFlash(): void {
    // Brief overlay flash element that fades. Independent of the Rosie-tality banner.
    const flash = document.createElement("div");
    flash.className = "robot-mode-flash";
    flash.innerHTML = `<div class="robot-mode-text">ROBOT MODE</div>`;
    this.bannerEl.appendChild(flash);
    window.setTimeout(() => flash.remove(), 1100);
  }

  private showTitleScreen(): void {
    this.state = "title";
    this.showOverlay();
    this.overlayEl.innerHTML = `
      <div class="w-full max-w-sm px-6 text-center">
        <div class="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#6fd6c8]">
          <span class="w-1.5 h-1.5 rounded-full bg-[#6fd6c8] animate-pulse"></span>
          A Tailos game
        </div>
        <h1 class="mt-4 text-5xl sm:text-6xl font-bold tracking-tight">
          Rosie<br/>
          <span class="text-[#6fd6c8]">Rumble</span>
        </h1>
        <p class="mt-4 text-sm text-white/65 leading-relaxed">
          Hotel night shift.<br/>
          Vacuum dust bunnies from the lobby and meeting rooms.<br/>
          Cut off rival Rosies before they cut off yours.
        </p>

        <button id="rosie-play"
          class="mt-8 w-full rounded-2xl bg-[#6fd6c8] text-[#0e1116] font-semibold py-4 text-lg
                 active:scale-[0.98] transition-transform">
          Start shift
        </button>
        <button id="rosie-board"
          class="mt-3 w-full rounded-2xl bg-white/8 border border-white/10 py-3 text-sm text-white/80
                 active:scale-[0.98] transition-transform">
          View leaderboard
        </button>

        <a href="${ROSIE_CTA_URL}" target="_blank" rel="noopener"
          class="block mt-6 rounded-2xl border border-[#6fd6c8]/30 bg-[#6fd6c8]/5 px-4 py-3 text-sm
                 text-[#8eece0] hover:bg-[#6fd6c8]/10 transition-colors">
          Tired of endless vacuuming?<br/>
          <span class="text-white/80">Check out the real Rosie →</span>
        </a>

        <div class="mt-6 text-[11px] text-white/30">
          Mouse or finger to steer · Every 25 dust = Robot Mode · Trap the others
        </div>
      </div>
    `;

    this.overlayEl
      .querySelector<HTMLButtonElement>("#rosie-play")
      ?.addEventListener("click", () => this.beginRun());

    this.overlayEl
      .querySelector<HTMLButtonElement>("#rosie-board")
      ?.addEventListener("click", () => this.showLeaderboardScreen());

    fetchLeaderboard().then((top) => {
      this.cachedTop = top;
    });
  }

  private async showLeaderboardScreen(): Promise<void> {
    this.overlayEl.innerHTML = `
      <div class="w-full max-w-sm px-6 text-center">
        <div class="text-xs uppercase tracking-[0.3em] text-[#6fd6c8]">All-time top 10</div>
        <h2 class="mt-2 text-3xl font-bold">Hotel night-shift leaderboard</h2>
        <div id="rosie-board-rows" class="mt-6 space-y-1.5 text-left"></div>
        <button id="rosie-back"
          class="mt-6 w-full rounded-2xl bg-white/8 border border-white/10 py-3 text-sm
                 active:scale-[0.98] transition-transform">
          Back
        </button>
      </div>
    `;
    const rows = this.overlayEl.querySelector<HTMLDivElement>("#rosie-board-rows")!;
    rows.innerHTML = `<div class="text-white/40 text-center text-sm py-6">Loading…</div>`;

    const top = await fetchLeaderboard();
    this.cachedTop = top;
    rows.innerHTML = renderBoardRows(top);

    this.overlayEl
      .querySelector<HTMLButtonElement>("#rosie-back")
      ?.addEventListener("click", () => this.showTitleScreen());
  }

  private showGameOver(): void {
    this.showOverlay();
    this.bannerEl.innerHTML = "";
    const score = this.world.player.score;
    const kills = this.world.player.kills;
    const canSubmit = score > 0;

    this.overlayEl.innerHTML = `
      <div class="w-full max-w-sm px-6 text-center">
        <div class="text-xs uppercase tracking-[0.3em] text-[#6fd6c8]">Shift over</div>
        <div class="mt-3 text-6xl font-bold tabular-nums">${score}</div>
        <div class="text-sm text-white/60 mt-1">dust bunnies vacuumed</div>
        ${
          kills > 0
            ? `<div class="mt-3 text-sm text-[#ffb47a] font-medium">
                 ${kills} Rosie${kills === 1 ? "" : "s"} eliminated
               </div>`
            : ""
        }

        ${
          canSubmit
            ? `
        <div id="rosie-submit-block" class="mt-6 text-left">
          <label class="block text-[10px] uppercase tracking-widest text-white/50 text-center">
            Add your name to the leaderboard
          </label>
          <div class="mt-2 flex gap-2">
            <input id="rosie-name" type="text" maxlength="16" autocomplete="off" autocorrect="off"
              spellcheck="false" placeholder="Anonymous"
              value="${escapeAttr(this.playerName)}"
              class="flex-1 min-w-0 rounded-2xl bg-white/8 border border-white/15 px-4 py-3 text-base
                     text-center placeholder:text-white/30 focus:outline-none focus:border-[#6fd6c8]
                     focus:bg-white/12" />
            <button id="rosie-submit"
              class="rounded-2xl bg-[#6fd6c8] text-[#0e1116] font-semibold px-4 text-sm
                     active:scale-[0.98] transition-transform">
              Submit
            </button>
          </div>
          <div id="rosie-rank-msg" class="mt-3 text-sm text-white/60 text-center min-h-[1.25rem]"></div>
        </div>
        `
            : `
        <div class="mt-6 text-sm text-white/50">No dust collected — no leaderboard entry this round.</div>
        `
        }

        <div id="rosie-mini-board" class="mt-4"></div>

        <button id="rosie-restart"
          class="mt-6 w-full rounded-2xl bg-[#6fd6c8] text-[#0e1116] font-semibold py-4 text-lg
                 active:scale-[0.98] transition-transform">
          Play again
        </button>
        <button id="rosie-home"
          class="mt-3 w-full rounded-2xl bg-white/8 border border-white/10 py-3 text-sm
                 active:scale-[0.98] transition-transform">
          Title screen
        </button>

        <a href="${ROSIE_CTA_URL}" target="_blank" rel="noopener"
          class="block mt-5 rounded-2xl border border-[#6fd6c8]/30 bg-[#6fd6c8]/5 px-4 py-3 text-sm
                 text-[#8eece0] hover:bg-[#6fd6c8]/10 transition-colors">
          Tired of endless vacuuming?<br/>
          <span class="text-white/80">Check out the real Rosie →</span>
        </a>
      </div>
    `;

    const board = this.overlayEl.querySelector<HTMLDivElement>("#rosie-mini-board");
    if (board) board.innerHTML = renderBoardRows(this.cachedTop);

    this.overlayEl
      .querySelector<HTMLButtonElement>("#rosie-restart")
      ?.addEventListener("click", () => this.beginRun(), { once: true });
    this.overlayEl
      .querySelector<HTMLButtonElement>("#rosie-home")
      ?.addEventListener("click", () => this.showTitleScreen(), { once: true });

    if (canSubmit) {
      const nameInput = this.overlayEl.querySelector<HTMLInputElement>("#rosie-name");
      const submitBtn = this.overlayEl.querySelector<HTMLButtonElement>("#rosie-submit");
      const rankMsg = this.overlayEl.querySelector<HTMLDivElement>("#rosie-rank-msg");

      nameInput?.addEventListener("input", () => {
        this.playerName = sanitizeName(nameInput.value);
      });

      submitBtn?.addEventListener(
        "click",
        async () => {
          if (!this.session) {
            if (rankMsg) rankMsg.textContent = "Leaderboard unreachable.";
            return;
          }
          const finalName = sanitizeName(nameInput?.value ?? this.playerName) || "Anonymous";
          this.playerName = finalName;
          storeName(finalName);
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = "…";
          }
          const result = await submitScore({
            sessionId: this.session.sessionId,
            token: this.session.token,
            name: finalName,
            score,
          });
          if (!result) {
            if (rankMsg) rankMsg.textContent = "Couldn't reach the leaderboard.";
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = "Submit";
            }
            return;
          }
          this.cachedTop = result.top;
          if (rankMsg) {
            if (result.accepted && result.rank) {
              rankMsg.innerHTML = `<span class="text-[#6fd6c8] font-semibold">New high score — #${result.rank} all-time</span>`;
            } else if (result.rank) {
              rankMsg.textContent = `Ranked #${result.rank}`;
            } else {
              rankMsg.textContent = "Beat the top 10 to make the board.";
            }
          }
          if (board) board.innerHTML = renderBoardRows(result.top, finalName, score);
          const submitBlock = this.overlayEl.querySelector<HTMLDivElement>("#rosie-submit-block");
          if (submitBlock) {
            const inputRow = submitBlock.querySelector("div.flex");
            const label = submitBlock.querySelector("label");
            label?.classList.add("hidden");
            inputRow?.classList.add("hidden");
          }
        },
        { once: true },
      );
    }

    fetchLeaderboard().then((top) => {
      this.cachedTop = top;
      if (board && !this.overlayEl.querySelector("#rosie-rank-msg")?.textContent) {
        board.innerHTML = renderBoardRows(top);
      }
    });
  }

  private showOverlay(): void {
    this.overlayEl.classList.remove("hidden");
    this.overlayEl.classList.add("flex");
  }
  private hideOverlay(): void {
    this.overlayEl.classList.add("hidden");
    this.overlayEl.classList.remove("flex");
    this.overlayEl.innerHTML = "";
  }
}

function renderBoardRows(
  top: LeaderboardEntry[],
  highlightName?: string,
  highlightScore?: number,
): string {
  if (top.length === 0) {
    return `<div class="text-white/40 text-center text-sm py-6">No scores yet — be the first.</div>`;
  }
  return top
    .map((e, i) => {
      const isYou =
        highlightName !== undefined &&
        e.name === highlightName &&
        e.score === highlightScore;
      const accent = isYou
        ? "bg-[#6fd6c8]/15 border-[#6fd6c8]/40"
        : "bg-white/5 border-white/8";
      return `
        <div class="flex items-center justify-between rounded-xl border ${accent} px-3 py-2">
          <div class="flex items-center gap-3">
            <span class="text-xs text-white/40 tabular-nums w-5 text-right">${i + 1}</span>
            <span class="text-sm font-medium">${escapeHtml(e.name)}</span>
          </div>
          <span class="text-sm font-semibold tabular-nums text-[#8eece0]">${e.score}</span>
        </div>
      `;
    })
    .join("");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, "&#39;");
}
